import React, { useState } from 'react';
import { CloseIcon, EditPencil, DeleteICon, PlusIcon, CheckIcon } from '../../../public/icons';

const CategoryDetailsModal = ({ isOpen, onClose, category, onDeleteQuestion, onEditQuestion }) => {
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [editingQuestionText, setEditingQuestionText] = useState('');
  const [loading, setLoading] = useState(false);
  const [deletingQuestionId, setDeletingQuestionId] = useState(null);
  const [savingQuestionId, setSavingQuestionId] = useState(null);

  if (!isOpen || !category) return null;

  const questions = category.questions || [];

  const handleStartEdit = (question) => {
    setEditingQuestionId(question._id || question.id);
    setEditingQuestionText(question.question);
  };

  const handleCancelEdit = () => {
    setEditingQuestionId(null);
    setEditingQuestionText('');
  };

  const handleSaveEdit = async (questionId) => {
    if (editingQuestionText.trim()) {
      setSavingQuestionId(questionId);
      setLoading(true);
      try {
        await onEditQuestion(questionId, editingQuestionText);
        setEditingQuestionId(null);
        setEditingQuestionText('');
      } catch (error) {
        console.error('Error saving question:', error);
      } finally {
        setSavingQuestionId(null);
        setLoading(false);
      }
    }
  };

  const handleDeleteClick = async (questionId, questionText) => {
    setDeletingQuestionId(questionId);
    if (onDeleteQuestion) {
      await onDeleteQuestion(questionId, questionText);
    }
    setDeletingQuestionId(null);
  };

  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
        onClick={onClose}
      />
      
      <div className="fixed right-0 top-0 h-full w-full md:w-[60%] lg:w-[50%] xl:w-[40%] bg-[#F9F6F2] z-40 overflow-y-auto transition-transform duration-300 ease-in-out shadow-xl">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between pb-5 pt-4 px-6">
            <div className="text-left">
              <h2 className="text-2xl font-bold text-[#1C2A46]">{category.name}</h2>
              <p className="text-[#6B635D] text-sm font-light mt-1">
                {category.description}
              </p>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-[#F8F8F8] border border-[#DBDBDB] rounded-sm transition-colors"
              disabled={loading}
            >
              <CloseIcon className="w-5 h-5 text-[#1C2A46]" />
            </button>
          </div>

          {/* Category Stats */}
          <div className="flex gap-6 mb-8 pb-6 border-b px-6 border-[#DBDBDB]">
            <div>
              <span className="text-2xl font-bold text-[#0E1628]">{category.questionsCount || questions.length}</span>
              <span className="text-xs font-light text-[#6B635D] ml-2">Questions</span>
            </div>
            <div>
              <span className="text-2xl font-bold text-[#0E1628]">{category.activeMemoirsCount || 0}</span>
              <span className="text-xs font-light text-[#6B635D] ml-2">Active Memoirs</span>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 pb-6">
            {/* Category Questions Section */}
            <div className="mb-6 border border-[#E4E4E4] rounded-sm">
              <div className="flex-col text-left px-4 py-4 border-b border-[#E4E4E4]">
                <h3 className="text-lg font-medium text-[#1C2A46]">Category Questions</h3>
                <p className="text-sm text-[#8A9099]">Manage all questions for this category</p>
              </div>

              {questions.length === 0 ? (
                <div className="p-8 text-center text-[#6B635D]">
                  No questions added to this category yet.
               
                </div>
              ) : (
                <div className="divide-y divide-[#E4E4E4]">
                  {questions.map((q) => {
                    const questionId = q._id || q.id;
                    const isEditing = editingQuestionId === questionId;
                    const isSaving = savingQuestionId === questionId;
                    const isDeleting = deletingQuestionId === questionId;
                    
                    return (
                      <div key={questionId} className="p-4 hover:bg-gray-50 transition-colors">
                        {isEditing ? (
                          // Edit mode
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={editingQuestionText}
                              onChange={(e) => setEditingQuestionText(e.target.value)}
                              className="flex-1 p-2 border-2 border-[#D2A146] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#D2A146]"
                              autoFocus
                              disabled={isSaving}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  handleSaveEdit(questionId);
                                }
                              }}
                            />
                            <button
                              onClick={() => handleSaveEdit(questionId)}
                              disabled={isSaving}
                              className="p-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Save"
                            >
                              {isSaving ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <CheckIcon size={16} color="#fff" />
                              )}
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              disabled={isSaving}
                              className="p-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Cancel"
                            >
                              <CloseIcon size={16} color="#fff" />
                            </button>
                          </div>
                        ) : (
                          // View mode
                          <div className="flex items-center justify-between">
                            <p className="text-sm text-[#0E1628] flex-1 text-left">{q.question}</p>
                            <div className="flex items-center gap-2">
                              <button 
                                className="p-1 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={() => handleStartEdit(q)}
                                disabled={isDeleting}
                              >
                                <EditPencil size={16} className="text-[#1E1E1E] w-5 h-5" />
                              </button>
                              <button 
                                className="p-1 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={() => handleDeleteClick(questionId, q.question)}
                                disabled={isDeleting}
                              >
                                {isDeleting ? (
                                  <div className="w-5 h-5 border-2 border-[#C03A2B] border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <DeleteICon size={20} className="text-[#C03A2B]" />
                                )}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CategoryDetailsModal;